/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { getRequiredFieldsForJurisdiction } from 'linced-app/utils/fields';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import config from 'linced-app/config/environment';
import { restartableTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import moment from 'moment';

export default class PreToCurrent extends Component {
  @action
  setStartDate(value) {
    this.startDate = value;
  }

  @action
  setStep(value) {
    this.step = value;
  }

  @service
  fetch;

  @service
  strings;

  @tracked
  step = 0;

  // `jurisdictionOverride` lets callers override the computed default; Ember < 4
  // allowed that implicitly (computed-property.override).
  jurisdictionOverride = null;

  @computed(
    'session.school.jurisdiction',
    'person.jurisdiction',
    'features.multiJurisdictionalEnabled',
    'jurisdictionOverride',
  )
  get jurisdiction() {
    if (this.jurisdictionOverride !== null) {
      return this.jurisdictionOverride;
    }

    return this.get('features.multiJurisdictionalEnabled')
      ? this.get('person.jurisdiction')
      : this.get('session.school.jurisdiction');
  }

  @computed('jurisdiction')
  get requiredFields() {
    return Object.freeze(getRequiredFieldsForJurisdiction(this.jurisdiction));
  }

  @tracked startDate = '';

  startDateValidation = Object.freeze([
    {
      message: 'First attendance date must be today or in the past',
      validate: (val) => {
        let parsed = moment(val);
        let now = moment();
        return parsed.isValid() && parsed.isSameOrBefore(now, 'd');
      },
    },
  ]);

  @computed('startDate')
  get startDateIsValid() {
    let now = moment();
    let parsed = moment(this.startDate);
    return !isEmpty(this.startDate) && parsed.isSameOrBefore(now, 'd');
  }

  @computed(
    'person.{id,homeroom,hasDirtyAttributes}',
    'enrolment.entryDate',
    'startDate',
    'startDateIsValid',
    'requiredFields.[]',
  )
  get ready() {
    let person = this.person;
    let isSaved = !person.hasDirtyAttributes;
    let dateChanged = this.startDate !== this.enrolment.entryDate;
    return (
      isSaved &&
      this.startDateIsValid &&
      !dateChanged &&
      !this.requiredFields.some((field) => {
        return isEmpty(person.get(`fields.${field.key}.value`));
      })
    );
  }

  didReceiveAttrs() {
    this.startDate = this.enrolment.entryDate;
    return super.didReceiveAttrs(...arguments);
  }

  onEnrolled() {}

  // A transfer pre-enrol's homeroom is a group at the new school, which the
  // store may not hold yet. Look it up when the step renders, and again when
  // the person changes.
  loadHomeroomOnInsert = modifier((_element, [person]) => {
    if (person) {
      this.loadHomeroom.perform(person);
    }
  });

  loadHomeroom = restartableTask(async (person) => {
    await person.loadHomeroom();
  });

  @action
  setHomeroom(learner, group) {
    let url = `${config.APP.apiHost}/groups/v4/groups/${group.id}/learners:updatemembership`;
    let data = {
      groupId: group.id,
      personId: learner.id,
      status: 1, // MemberStatus.CURRENT = 1
    };
    return this.fetch.post(url, { data }).then(() => {
      return learner.reload();
    });
  }

  @action
  saveFields() {
    // NOTE: We need to store startDate here as `this.startDate` will be reset by `didReceiveAttrs()`, which
    // will trigger when the Person record changes or the Enrolment record is reloaded.
    let startDate = this.startDate;
    let chain = Promise.resolve();
    if (this.person.hasDirtyAttributes) {
      chain = this.person.save();
    }
    if (this.startDateIsValid && this.startDate !== this.enrolment.entryDate) {
      // Update the enrolment record before saving to ensure that we don't accidentally wipe anything
      // which was updated by the Person update.
      chain.then(() => {
        return this.enrolment.reload().then((enrolment) => {
          enrolment.set('entryDate', startDate);
          return enrolment.save();
        });
      });
    }
  }

  @action
  enrol() {
    // Update the enrolment record before saving to ensure that we don't accidentally wipe anything
    // which was updated by the Person update in the previous step.
    this.enrolment.reload().then((enrolment) => {
      enrolment.set('status', EnrolmentStatus.CURRENT);
      enrolment.save().then(() => {
        this.onEnrolled();
      });
    });
  }
}
